import { Directive, ElementRef, OnDestroy } from '@angular/core';
import { Chart, registerables } from 'chart.js';

@Directive()
export abstract class abstractChart implements OnDestroy {
  abstract getGraph(): ElementRef<HTMLCanvasElement> | undefined;
  abstract getLabel(): string;

  private _legend: boolean = true;
  private _type: any = ''; // pie, bar, line
  private _chart: Chart | undefined;
  private _resizeObserver: ResizeObserver | undefined;

  constructor(type: string, legend: boolean) {
    this._type = type;
    this._legend = legend;

    if (type == 'pie') {
      this._legend = true;
    }
  }

  ngOnDestroy(): void {
    if (this._resizeObserver) {
      this._resizeObserver?.disconnect();
    }
    this._chart?.destroy();
  }

  /* We use this setter instead of binding directly to an @Input because
    // charts don't detect changes, and we can't detect changes to an array
    // without a lot of overhead. This just seems more reasonable.
    */
  // options for [colors]:
  // 1) An array of hex colors that's indexed according to newData (one-to-one match)
  // 2) An object with default set. Example: { default: "#ff0000" }
  // 3) An object with a field corresponding to each label in newData
  // 4) Not set: a random color is chosen
  public setData(newData: any[], colors: any) {
    this._refreshGraph(newData, colors);
  }

  private _refreshGraph(data: any[], colors: any) {
    let chart: ElementRef<HTMLCanvasElement> | undefined = this.getGraph();

    if (chart) {
      Chart.register(...registerables);

      const ctx = chart.nativeElement.getContext('2d');

      if (ctx) {
        let graphData: any = {
          labels: data.map((row) => row.year),
          datasets: [
            {
              label: this.getLabel(),
              data: data.map((row) => row.count),
            },
          ],
        };

        if (!graphData.datasets[0].backgroundColor) {
          graphData.datasets[0].backgroundColor = [];
        }

        for (var i = 0; i < data.length; i++) {
          if (colors && colors[data[i].year]) {
            graphData.datasets[0].backgroundColor.push(
              colors[data[i].year] as never,
            );
          } else if (colors.length > 0) {
            graphData.datasets[0].backgroundColor.push(colors[i] as never);
          } else if (colors && colors['default']) {
            graphData.datasets[0].backgroundColor.push(
              colors['default'] as never,
            );
          } else {
            graphData.datasets[0].backgroundColor.push(
              this._getRandomColorHex() as never,
            );
          }
        }

        this._chart = new Chart(ctx, {
          type: this._type,
          data: graphData,
          options: {
            maintainAspectRatio: false,
            responsive: false, // Angular will crash the app if this is true! See below for alternative.
            plugins: {
              legend: {
                display: this._legend,
              },
            },
          },
        });

        // Alternative to resonsive:true
        if (this._chart) {
          this._resizeObserver = new ResizeObserver(() => {
            this._chart?.update();
          });
          let canvas = this.getGraph();
          if (canvas) {
            this._resizeObserver.observe(canvas.nativeElement);
          }
        }
      }
    }
  }

  private _getRandomColorHex(): string {
    return (
      '#' +
      Math.round(Math.random() * 254).toString(16) +
      Math.round(Math.random() * 254).toString(16) +
      Math.round(Math.random() * 254).toString(16)
    );
  }
}
